/**
 * Table-of-contents helpers shared by the peer-review Full Document tab and the
 * Final Review document editor. Both render section headings whose DOM ids are
 * stamped via buildHeadingId, so the sidebar links resolve to the same ids.
 */
import { buildHeadingId } from './headingAnchors';
import { closesFence, parseFenceLine } from './markdownFences';

export interface TocSubHeading {
  text: string;
  id: string;
  level: number;
}

export interface TocEntry {
  sectionId: string;
  title: string;
  subHeadings: TocSubHeading[];
}

export interface DocumentTocSection {
  id: string;
  title: string;
  content?: string | null;
}

const ADR_SECTION_PATTERN = /architecture decision records/i;

/**
 * Parse a section's markdown into its sub-headings. Skips fenced code blocks
 * (so a `#comment` inside a diagram isn't listed) and the section's own title.
 * The DOM id is derived from the RAW heading text (to stay in sync with the
 * rendered heading), while the display label strips Markdown link syntax
 * ([label](url) -> label) — ADR entries arrive as
 * `#### N. [ADR Title](/project/.../adr/.../content)`.
 */
export const parseSectionSubHeadings = (
  content: string,
  sectionTitle: string,
  idPrefix: string,
): TocSubHeading[] => {
  const subHeadings: TocSubHeading[] = [];
  const normalizedTitle = sectionTitle.trim().toLowerCase();
  let openFenceMarker = '';
  content.split('\n').forEach((line) => {
    const fence = parseFenceLine(line);
    if (fence && (!openFenceMarker || closesFence(fence, openFenceMarker))) {
      openFenceMarker = openFenceMarker ? '' : fence.marker;
      return;
    }
    if (openFenceMarker) {
      return;
    }
    const match = /^(#{1,6})\s+(.*)$/.exec(line);
    if (!match) {
      return;
    }
    const rawText = (match[2] ?? '').trim();
    if (!rawText || rawText.toLowerCase() === normalizedTitle) {
      return;
    }
    const id = buildHeadingId(idPrefix, rawText);
    if (!id) {
      return;
    }
    const text = rawText.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').trim();
    subHeadings.push({ text, id, level: match[1].length });
  });
  return subHeadings;
};

/**
 * Build the table-of-contents entries for a document sidebar. Only the
 * Architecture Decision Records section expands into sub-items (one per ADR);
 * every other section stays collapsed to just its header. `idPrefix(sectionId)`
 * must match the headingIdPrefix passed to that section's MonacoMarkdownEditor.
 */
export const buildDocumentToc = (
  sections: DocumentTocSection[],
  idPrefix: (sectionId: string) => string,
): TocEntry[] =>
  sections.map((section) => {
    const isAdrSection = ADR_SECTION_PATTERN.test(section.title);
    const subHeadings = isAdrSection
      ? parseSectionSubHeadings(
        section.content ?? '',
        section.title,
        idPrefix(section.id),
      )
      : [];
    return { sectionId: section.id, title: section.title, subHeadings };
  });
